import cn from 'classnames';
import { Core, Drawer, Form, Layout, Modal, Localization } from 'connex-cds';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import style from './style';
import { EnterRecipientsEditor } from './recipients/enter-recipients/EnterRecipientsEditor';
import { useCompanySetup } from '../../../../../MasterDataProvider';
import { PrivacyPolicy } from './recipients/privacy-policy/PrivacyPolicy';
import TermsAndConditions from './terms-and-conditions/TermsAndConditions';
import { UnFinalize } from '../../../../modal/UnFinalize';

const { Button } = Core;
const { useDrawerContext } = Drawer;
const { Row } = Layout;

const Styled = styled.div`
  ${style}
`;
const MANDATORY = 'MANDATORY';
const TERMS = 'TERMS';
export const AcceptanceSignature = ({ ticketId, onCancel, activeFormsAndWaivers = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const translateMessage = Localization.useTranslateMessage();
  const privacyPolicyTitle = translateMessage('privacyPolicy');
  const termsAndConditionsTitle = translateMessage('termsAndConditions');
  const { userLocale } = Localization.useLocalizationContext();
  const { Components, values, setFieldValue } = Form.useFormContext();
  const { closeDrawer, openDrawer } = useDrawerContext();
  const { openModal, closeModal } = Modal.useModalContext();
  const companySetup = useCompanySetup();

  const isTermsAndConditionsMandatory = React.useMemo(() => {
    return activeFormsAndWaivers?.some(item => item?.mandatoryWorkflow === MANDATORY && item?.type === TERMS);
  }, [activeFormsAndWaivers]);

  const disabled = React.useMemo(() => {
    const signerName = values?.signature?.signerName;
    const hasSignerName = signerName && signerName?.trim() !== '';
    return !values?.signature?.image || !hasSignerName;
  }, [values?.signature]);

  const isEarlyAcceptance = React.useMemo(() => {
    if (!companySetup?.data?.isEarlySignatureEnabled) {
      return false;
    }

    const searchParams = new URLSearchParams(location?.search);
    return searchParams.get('showEarlyAcceptance') ?? false;
  }, [location?.search, companySetup?.data?.isEarlySignatureEnabled]);

  const handleOkClick = React.useCallback(() => {
    if (!isEarlyAcceptance) {
      setFieldValue('customerStatus', 'ACCEPTED');
      closeModal();
      closeDrawer();
    }

    if (isEarlyAcceptance) {
      const onOkay = password => {
        if (companySetup?.data?.unFinalizePassword === password) {
          closeModal();
          closeDrawer();
          navigate('../ticketDetail');
        }
      };

      if (!companySetup?.data?.unFinalizePassword) {
        navigate('../ticketDetail');
      }

      if (companySetup?.data?.unFinalizePassword) {
        openModal({
          title: 'Modal',
          component: <UnFinalize onOkay={onOkay} onCancel={closeModal} />,
        });
      }
    }
  }, [closeDrawer, setFieldValue, isEarlyAcceptance]);

  const handleEnterRecipients = React.useCallback(() => {
    openDrawer({
      onClose: onCancel,
      titleStringId: 'enterRecipientsButton',
      component: <EnterRecipientsEditor ticketId={ticketId} handleAcceptCancel={onCancel} />,
      width: '100%',
    });
  }, []);

  const handlePrivacyPolicy = React.useCallback(() => {
    openModal({
      onClose: closeModal,
      titleStringId: privacyPolicyTitle,
      component: <PrivacyPolicy userLocale={userLocale} />,
    });
  }, [closeModal, openModal, privacyPolicyTitle, userLocale]);

  const handleTermsAndConditions = React.useCallback(() => {
    openModal({
      onClose: closeModal,
      titleStringId: termsAndConditionsTitle,
      component: <TermsAndConditions activeFormsAndWaivers={activeFormsAndWaivers} showModal />,
    });
  }, [closeModal, openModal, termsAndConditionsTitle, activeFormsAndWaivers]);

  return (
    <Styled className={cn('signature-comment')}>
      <Components.Signature edit ticketId={ticketId} />

      <Row className="actions">
        <Row>
          <Button
            type="primary"
            danger
            onClick={onCancel}
            stringId="cancel-signature-button"
            data-testid="cancel-signature-button"
          />
          <Button
            type="primary"
            disabled={disabled}
            onClick={handleOkClick} // this
            stringId="submit-signature-button"
            data-testid="ok-button"
          />
        </Row>
      </Row>

      {companySetup?.data?.isPDFRecipientListEnable && (
        <Row className="recipients">
          <Button
            onClick={handleEnterRecipients}
            stringId="enterRecipientsButton"
            data-testid="enter-recipients-button"
          />
          {values?.hasSentRecipients && (
            <span>
              <i className="fa-regular fa-check-circle iconValid" />
            </span>
          )}
        </Row>
      )}

      <div
        className={cn('privacy-policy-link', { 'no-list-recipients': !companySetup?.data?.isPDFRecipientListEnable })}
      >
        <Core.Link onClick={handlePrivacyPolicy} testId="privacy-policy-link" stringId="privacyPolicy" />
        {isTermsAndConditionsMandatory === false && (
          <>
            {' | '}
            <Core.Link
              onClick={handleTermsAndConditions}
              testId="terms-and-conditions-link"
              stringId="termsAndConditions"
            />
          </>
        )}
      </div>
    </Styled>
  );
};
